import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import './EvidenceDrawer.css';

export const FIELD_LABELS = {
  name: 'Full Name',
  jobTitle: 'Job Title',
  company: 'Company',
  email: 'Email',
  phone: 'Phone',
  linkedinUrl: 'LinkedIn Profile',
  companyWebsite: 'Company Website',
};

export const getFieldIcon = (fieldName) => {
  switch (fieldName) {
    case 'email':
      return 'far fa-envelope';
    case 'phone':
      return 'fas fa-phone';
    case 'jobTitle':
      return 'fas fa-briefcase';
    case 'company':
      return 'far fa-building';
    case 'name':
      return 'far fa-user';
    case 'linkedinUrl':
      return 'fab fa-linkedin';
    case 'companyWebsite':
      return 'fas fa-globe';
    default:
      return 'fas fa-tag';
  }
};

export const formatFieldLabel = (fieldName, label) => {
  if (label && label !== fieldName) return label;
  if (FIELD_LABELS[fieldName]) return FIELD_LABELS[fieldName];
  return String(fieldName || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
};

export const getLeadFallbackValues = (field, lead) => {
  if (!lead) return [];
  if (field === 'email') {
    const list = [];
    const add = (val) => {
      if (!val) return;
      if (typeof val === 'string') {
        val.split(/[,;\n]+/).forEach((e) => {
          const t = e.trim();
          if (t && !list.includes(t)) list.push(t);
        });
      } else if (Array.isArray(val)) {
        val.forEach(add);
      } else if (typeof val === 'object' && val.email) {
        add(val.email);
      }
    };
    if (lead.emails) add(lead.emails);
    if (lead.email_addresses) add(lead.email_addresses);
    if (lead.email) add(lead.email);
    return list;
  }
  if (field === 'phone') {
    const list = [];
    const add = (val) => {
      if (!val) return;
      if (typeof val === 'string') {
        val.split(/[,;\n]+/).forEach((p) => {
          const t = p.trim();
          if (t && !list.includes(t)) list.push(t);
        });
      } else if (Array.isArray(val)) {
        val.forEach(add);
      } else if (typeof val === 'object') {
        add(val.number || val.phone);
      }
    };
    if (lead.phones) add(lead.phones);
    if (lead.phone_numbers) add(lead.phone_numbers);
    if (lead.phone) add(lead.phone);
    return list;
  }
  if (field === 'jobTitle') {
    const v = lead.jobTitle || lead.job_title || lead.title || lead.role || '';
    return v && String(v).trim() ? [String(v).trim()] : [];
  }
  if (field === 'company') {
    const v = lead.company || lead.company_name || lead.companyName || '';
    return v && String(v).trim() ? [String(v).trim()] : [];
  }
  if (field === 'name') {
    const v =
      lead.name ||
      lead.full_name ||
      (lead.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : '');
    return v && String(v).trim() ? [String(v).trim()] : [];
  }
  return lead[field] && String(lead[field]).trim() ? [String(lead[field]).trim()] : [];
};

export const getLeadFallbackValue = (field, lead) => {
  const vals = getLeadFallbackValues(field, lead);
  return vals.join(', ');
};

const EvidencePanel = ({
  lead,
  apiEndpointPrefix = '/lead-pipeline',
  initialTab = 'fields',
  highlightField = null,
  onOpenLockModal,
  onLeadUpdated,
  canEdit = true,
  refreshTrigger = 0,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [resolvingConflictId, setResolvingConflictId] = useState(null);

  const entityId = lead?.id || lead?.prospect_id || lead?.shared_item_id;

  const fetchEvidence = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      setActionError('');
      const res = await axios.get(`${apiEndpointPrefix}/${entityId}/evidence`);
      if (res.data?.success) {
        setLedgerData(res.data.data);
      }
    } catch (err) {
      console.error('[EvidencePanel] Error fetching evidence:', err);
      setActionError('Failed to load evidence ledger.');
    } finally {
      setLoading(false);
    }
  }, [apiEndpointPrefix, entityId]);

  useEffect(() => {
    if (entityId) {
      fetchEvidence();
    }
  }, [entityId, fetchEvidence, lead?.field_locks, refreshTrigger]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const conflicts = ledgerData?.conflicts || [];
  const fields = ledgerData?.fields || {};
  const history = ledgerData?.history || [];

  const handleUnlock = async (fieldName, fieldLabel) => {
    if (
      !window.confirm(
        `Are you sure you want to unlock ${fieldLabel || fieldName}? Automated tools will be allowed to update it.`
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      setActionError('');
      await axios.post(`${apiEndpointPrefix}/${entityId}/evidence/unlock`, { fieldName });
      setActionSuccess(`Unlocked ${fieldLabel || fieldName}`);
      setTimeout(() => setActionSuccess(''), 3000);
      await fetchEvidence();
      if (onLeadUpdated) onLeadUpdated(fieldName);
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to unlock field');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveConflict = async (conflictId, resolutionAction) => {
    try {
      setResolvingConflictId(conflictId);
      setActionError('');
      const res = await axios.post(`${apiEndpointPrefix}/${entityId}/evidence/conflict/resolve`, {
        conflictId,
        resolutionAction,
        reason:
          resolutionAction === 'keep_current'
            ? 'Sales rep chose to keep current verified value'
            : 'Sales rep accepted incoming provider data',
      });
      if (res.data?.success) {
        setActionSuccess(
          resolutionAction === 'keep_current'
            ? 'Kept verified value. Conflict dismissed.'
            : 'Accepted provider value as new canonical data.'
        );
        setTimeout(() => setActionSuccess(''), 4000);
        await fetchEvidence();
        if (onLeadUpdated) onLeadUpdated();
      }
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to resolve conflict');
    } finally {
      setResolvingConflictId(null);
    }
  };

  const getProvenanceBadgeClass = (pClass) => {
    switch (pClass) {
      case 'user_verified':
        return 'prov-pill-verified';
      case 'provider_supplied':
        return 'prov-pill-provider';
      case 'extracted':
        return 'prov-pill-extracted';
      case 'inferred':
        return 'prov-pill-inferred';
      default:
        return 'prov-pill-default';
    }
  };

  return (
    <div className="evidence-panel-container">
      {/* Feedback Banners */}
      {actionSuccess && (
        <div className="evidence-banner evidence-banner-success">
          <i className="fas fa-check-circle" /> {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="evidence-banner evidence-banner-error">
          <i className="fas fa-exclamation-circle" /> {actionError}
        </div>
      )}

      {/* Panel Sub-Tabs Navigation (Consistent Pill Navbar) */}
      <div className="crm-meetings-action-bar evidence-panel-nav">
        <div className="crm-meetings-filter-pills evidence-panel-tabs">
          <button
            type="button"
            className={`crm-meeting-filter-pill evidence-panel-tab ${activeTab === 'fields' ? 'active' : ''}`}
            onClick={() => setActiveTab('fields')}
          >
            Tracked Fields & Locks ({Object.keys(fields).length})
          </button>
          <button
            type="button"
            className={`crm-meeting-filter-pill evidence-panel-tab ${activeTab === 'conflicts' ? 'active' : ''}`}
            onClick={() => setActiveTab('conflicts')}
          >
            Conflicts ({conflicts.length})
          </button>
          <button
            type="button"
            className={`crm-meeting-filter-pill evidence-panel-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Audit Ledger ({history.length})
          </button>
        </div>

        <button
          type="button"
          className="evidence-panel-refresh-btn"
          onClick={fetchEvidence}
          title="Refresh evidence data"
          disabled={loading}
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Panel Body Content */}
      <div className="evidence-panel-body">
        {loading && !ledgerData && (
          <div className="evidence-loading-state">
            <i className="fas fa-spinner fa-spin fa-2x" />
            <p>Loading evidence ledger...</p>
          </div>
        )}

        {/* TAB 1: TRACKED FIELDS & LOCKS */}
        {activeTab === 'fields' && (
          <div className="evidence-tab-pane">
            <div className="evidence-section-intro-compact">
              <i className="fas fa-shield-alt" />
              <span>
                <strong>Resolution Precedence Hierarchy:</strong> User Verified (Locked) &gt;
                Licensed Provider (Lusha/Apollo) &gt; Web Extracted &gt; AI Inferred.
              </span>
            </div>

            <div className="evidence-property-card">
              {Object.keys(fields).length === 0 ? (
                <div
                  className="evidence-empty-msg"
                  style={{ textAlign: 'center', padding: '36px' }}
                >
                  No tracked fields recorded for this lead yet.
                </div>
              ) : (
                Object.entries(fields).map(([fKey, fData]) => {
                  const isHighlighted = highlightField === fKey;
                  const userReadableLabel = formatFieldLabel(fKey, fData.label);
                  const fallbackValues = getLeadFallbackValues(fKey, lead);
                  const displayValues =
                    Array.isArray(fData.values) && fData.values.length > 0
                      ? fData.values
                      : fallbackValues.length > 0
                        ? fallbackValues
                        : fData.currentValue && String(fData.currentValue).trim()
                          ? String(fData.currentValue)
                              .split(/[,;\n]+/)
                              .map((s) => s.trim())
                              .filter(Boolean)
                          : [];
                  const hasValue = displayValues.length > 0;
                  const effectiveValue = displayValues.join(', ');
                  const checkObjLock = (item) => {
                    if (!item) return false;
                    if (item === true) return true;
                    if (typeof item === 'object') {
                      if (item.isLocked !== undefined) return Boolean(item.isLocked);
                      if (item.is_locked !== undefined) return Boolean(item.is_locked);
                      if (item.lockedAt || item.locked_at || item.reason || item.lockReason)
                        return true;
                    }
                    return false;
                  };

                  const isLocked = Boolean(
                    fData.isLocked ||
                    checkObjLock(lead?.field_locks?.[fKey]) ||
                    (fKey === 'email' &&
                      (checkObjLock(lead?.field_locks?.email) ||
                        checkObjLock(lead?.field_locks?.emails))) ||
                    (fKey === 'phone' &&
                      (checkObjLock(lead?.field_locks?.phone) ||
                        checkObjLock(lead?.field_locks?.phones) ||
                        checkObjLock(lead?.field_locks?.phone_number) ||
                        checkObjLock(lead?.field_locks?.phoneNumber))) ||
                    (fKey === 'jobTitle' &&
                      (checkObjLock(lead?.field_locks?.jobTitle) ||
                        checkObjLock(lead?.field_locks?.job_title) ||
                        checkObjLock(lead?.field_locks?.title) ||
                        checkObjLock(lead?.field_locks?.role))) ||
                    (fKey === 'company' &&
                      (checkObjLock(lead?.field_locks?.company) ||
                        checkObjLock(lead?.field_locks?.company_name) ||
                        checkObjLock(lead?.field_locks?.companyName))) ||
                    (fKey === 'name' &&
                      (checkObjLock(lead?.field_locks?.name) ||
                        checkObjLock(lead?.field_locks?.full_name) ||
                        checkObjLock(lead?.field_locks?.fullName) ||
                        checkObjLock(lead?.field_locks?.contact_name) ||
                        checkObjLock(lead?.field_locks?.contactName)))
                  );

                  return (
                    <div
                      key={fKey}
                      className={`evidence-property-row ${isLocked ? 'is-locked' : ''} ${
                        isHighlighted ? 'is-highlighted' : ''
                      }`}
                    >
                      {/* Left: Field Name & Icon */}
                      <div className="evidence-prop-label-col">
                        <div className="evidence-prop-label-main">
                          <i className={`${getFieldIcon(fKey)} evidence-prop-icon`} />
                          <span className="evidence-prop-name">{userReadableLabel}</span>
                        </div>
                        {fData.hasConflict && (
                          <span
                            className="evidence-conflict-pill"
                            onClick={() => setActiveTab('conflicts')}
                            title="Click to resolve conflicting provider data"
                          >
                            <i className="fas fa-bolt" /> Conflict
                          </span>
                        )}
                      </div>

                      {/* Center: Full Value & Provenance Metadata */}
                      <div className="evidence-prop-value-col">
                        <div className="evidence-prop-value-row">
                          {hasValue ? (
                            <div className="evidence-prop-values-list">
                              {displayValues.map((val, idx) => (
                                <span key={idx} className="evidence-prop-value">
                                  {val}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="evidence-prop-empty">Not recorded</span>
                          )}
                        </div>

                        {hasValue && (
                          <div className="evidence-prop-meta-row">
                            <span
                              className={`meta-pill ${getProvenanceBadgeClass(isLocked ? 'user_verified' : fData.provenanceClass)}`}
                            >
                              {isLocked ? 'User Verified' : fData.source || 'System'}
                            </span>
                            {(isLocked || fData.confidence != null) && (
                              <span className="evidence-conf-pill">
                                {isLocked ? '100%' : `${Math.round(fData.confidence)}%`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: Protection Status & Action */}
                      <div className="evidence-prop-action-col">
                        {canEdit ? (
                          isLocked ? (
                            <div className="evidence-table-lock-cell">
                              <span
                                className="evidence-locked-pill"
                                onClick={() => setActiveTab('history')}
                                title={
                                  fData.lockReason || lead?.field_locks?.[fKey]?.reason
                                    ? `Locked by ${fData.lockedBy || lead?.field_locks?.[fKey]?.lockedBy || 'team'}: "${fData.lockReason || lead?.field_locks?.[fKey]?.reason}". Click to view Audit Ledger.`
                                    : 'Field is protected. Click to view Audit Ledger.'
                                }
                              >
                                <i className="fas fa-lock" /> Locked
                              </span>
                              <button
                                type="button"
                                className="evidence-table-unlock-btn"
                                onClick={() => handleUnlock(fKey, userReadableLabel)}
                                disabled={loading}
                                title="Unlock this field"
                              >
                                Unlock
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className={`evidence-action-btn evidence-lock-btn ${!hasValue ? 'is-disabled' : ''}`}
                              onClick={() => {
                                if (hasValue && onOpenLockModal) {
                                  onOpenLockModal(fKey, userReadableLabel, effectiveValue);
                                }
                              }}
                              disabled={loading || !hasValue}
                              title={
                                hasValue
                                  ? `Lock ${userReadableLabel} to prevent automated sync overwrites`
                                  : 'Cannot lock an empty field'
                              }
                            >
                              <i className="fas fa-lock" /> Lock Field
                            </button>
                          )
                        ) : isLocked ? (
                          <span
                            className="evidence-locked-pill"
                            onClick={() => setActiveTab('history')}
                            title="Field is protected. Click to view Audit Ledger."
                          >
                            <i className="fas fa-lock" /> Locked
                          </span>
                        ) : (
                          <span className="evidence-unlocked-pill">Unlocked</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* TAB 2: ACTIVE CONFLICTS */}
        {activeTab === 'conflicts' && (
          <div className="evidence-tab-pane">
            {conflicts.length === 0 ? (
              <div className="evidence-empty-conflicts">
                <div className="evidence-clean-icon">
                  <i className="fas fa-check-circle" />
                </div>
                <h3>Zero Data Conflicts</h3>
                <p>
                  All external provider syncs align with your records, or locked fields have had
                  conflicts resolved.
                </p>
              </div>
            ) : (
              <div className="evidence-conflicts-list">
                <div className="evidence-conflicts-alert-banner">
                  <i className="fas fa-bolt" />
                  <div>
                    <strong>External Provider Conflicts Detected</strong>
                    <p>
                      An external provider (Lusha, Apollo, or Hunter) found new data that differs
                      from your verified information. Review and choose which value to preserve.
                    </p>
                  </div>
                </div>

                {conflicts.map((conf) => {
                  const isBusy = resolvingConflictId === conf.id;
                  return (
                    <div key={conf.id} className="evidence-conflict-card">
                      <div className="evidence-conflict-card-header">
                        <span className="conflict-field-tag">
                          Field: <strong>{conf.field_name}</strong>
                        </span>
                        <span className="conflict-timestamp">
                          Detected {new Date(conf.created_at).toLocaleString()}
                        </span>
                      </div>

                      <div className="conflict-comparison-grid">
                        {/* Left Side: Current Verified Value */}
                        <div className="conflict-side current-side">
                          <div className="conflict-side-badge">
                            <i className="fas fa-shield-alt" /> Current (Verified)
                          </div>
                          <div className="conflict-value-display">
                            {conf.current_value || <em>(Empty)</em>}
                          </div>
                          <div className="conflict-meta-info">
                            <span>Locked / Active in CRM</span>
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              className="conflict-btn conflict-btn-keep"
                              onClick={() => handleResolveConflict(conf.id, 'keep_current')}
                              disabled={isBusy}
                            >
                              {isBusy ? (
                                <i className="fas fa-spinner fa-spin" />
                              ) : (
                                <i className="fas fa-check" />
                              )}
                              Keep Current Verified
                            </button>
                          )}
                        </div>

                        <div className="conflict-vs-divider">VS</div>

                        {/* Right Side: Competing Provider Value */}
                        <div className="conflict-side incoming-side">
                          <div className="conflict-side-badge provider-badge">
                            <i className="fas fa-cloud-download-alt" /> {conf.source || 'Provider'}
                          </div>
                          <div className="conflict-value-display highlight">
                            {conf.competing_value || <em>(Empty)</em>}
                          </div>
                          <div className="conflict-meta-info">
                            <span>
                              Confidence:{' '}
                              {conf.confidence_score ? `${conf.confidence_score}%` : 'N/A'}
                            </span>
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              className="conflict-btn conflict-btn-accept"
                              onClick={() => handleResolveConflict(conf.id, 'accept_incoming')}
                              disabled={isBusy}
                            >
                              {isBusy ? (
                                <i className="fas fa-spinner fa-spin" />
                              ) : (
                                <i className="fas fa-exchange-alt" />
                              )}
                              Accept Provider Value
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: AUDIT HISTORY TIMELINE */}
        {activeTab === 'history' && (
          <div className="evidence-tab-pane">
            <div className="evidence-timeline">
              {history.length === 0 ? (
                <p className="evidence-empty-msg">No audit history recorded yet.</p>
              ) : (
                history.map((ev, idx) => {
                  const pic = ev.locked_by_name || ev.actor_id || ev.locked_by_user_id;
                  const note = ev.lock_reason || ev.verification_note;
                  const source =
                    ev.source_name || ev.source || (ev.is_locked ? 'User Verified' : 'Manual User');
                  const conf = ev.confidence != null ? ev.confidence : ev.confidence_score;
                  const displayFieldName = formatFieldLabel(ev.field_name, ev.field_name);

                  return (
                    <div key={ev.id || idx} className="evidence-timeline-item">
                      <div className="timeline-marker" />
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-action">
                            {ev.is_conflict ? (
                              <span className="text-conflict">⚡ Conflict Detected</span>
                            ) : ev.is_locked ? (
                              <span className="text-locked">🔒 Field Locked</span>
                            ) : (
                              <span>Verified Update</span>
                            )}
                          </span>
                          <span className="timeline-date">
                            {new Date(ev.created_at).toLocaleString()}
                          </span>
                        </div>

                        <div className="timeline-details">
                          <strong>{displayFieldName}:</strong>{' '}
                          {ev.is_conflict ? (
                            <span>
                              Current: <code>{ev.current_value}</code> vs Provider:{' '}
                              <code>{ev.competing_value}</code>
                            </span>
                          ) : (
                            <code>{ev.field_value || '(Empty)'}</code>
                          )}
                        </div>

                        <div className="timeline-meta">
                          <span className="timeline-source">Source: {source}</span>
                          {conf != null && (
                            <span className="timeline-conf">Confidence: {Math.round(conf)}%</span>
                          )}
                          {pic && (
                            <span className="timeline-actor">
                              PIC: <strong>{pic}</strong>
                            </span>
                          )}
                        </div>

                        {note && (
                          <div className="timeline-note">
                            <i className="fas fa-quote-left text-[9px]" /> Note: "{note}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvidencePanel;
